import { stringify } from 'querystring'

import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { useRouter } from 'next/router'

import { Badge } from '@wartek-id/badge'
import { Text } from '@wartek-id/text'
import { Icon } from '@wartek-id/icon'
import { useToast } from '@wartek-id/toast'

import { useScreenSizeContext } from 'utils/hooks/context/useScreenSizeContext'
import { GATrackEvent } from 'utils/googleAnalytics'
import { AksiNyataSessionState } from 'types/microlearning'

const NeedRevisionCard = ({
  name,
  aksiNyataUrl,
  onCardClick,
  selectedOptionTitle,
}) => (
  <div data-testid="aksinyata-card" className="py-5">
    <div className="mb-2">
      <Text variant="heading-md">Aksi Nyata</Text>
      <Text variant="body" color="subdued">
        Praktikkan pemahaman terhadap topik ini
      </Text>
    </div>

    <div className="relative flex p-4 w-full mb-3 bg-white rounded border border-subdued hover:cursor-pointer">
      <div className="w-8 h-8 rounded-full flex flex-grow-0 flex-shrink-0 items-center justify-center bg-warning">
        <Icon
          data-testid="completed_indicator"
          as="i"
          color="inverse"
          fontSize="sm"
          className="!text-base !leading-4"
        >
          border_color
        </Icon>
      </div>
      <div className="ml-4 flex-auto">
        <div className="mb-[2px] leading-tight">
          <Text
            data-testid="card-link"
            data-card-url={aksiNyataUrl}
            onClick={() => {
              onCardClick(aksiNyataUrl)
            }}
            className="overlay-link cursor-pointer"
            variant="heading-md"
          >
            {name}
          </Text>
        </div>
        <div className="flex items-center">
          <div>
            <Text
              className="line-clamp-1"
              as="span"
              variant="body-sm"
              color="subdued"
            >
              {selectedOptionTitle}
            </Text>
          </div>
        </div>
        <div className="mt-3">
          <Badge data-testid="aksinyata-status" variant="warning">
            Perbaiki Aksi Nyata
          </Badge>
        </div>
      </div>
    </div>
  </div>
)

interface AksiNyataCardProps {
  topikId: number | string
  name: string
  optionsAmount: number
  aksiNyataSessionState: AksiNyataSessionState
  aksiNyataId?: string | number | null
  selectedOptionTitle?: string
  isLocked?: boolean
}

const AksiNyataCard = ({
  topikId,
  name,
  optionsAmount,
  selectedOptionTitle,
  aksiNyataId = null,
  isLocked = true,
  aksiNyataSessionState = AksiNyataSessionState.Undetermined,
}: AksiNyataCardProps) => {
  const [aksiNyataUrl, setAksiNyataUrl] = useState('')
  const { isDesktop } = useScreenSizeContext()
  const isOnAppeal = aksiNyataSessionState === AksiNyataSessionState.Appeal
  const router = useRouter()
  const toast = useToast()
  useEffect(() => {
    let cardUrl = ''
    switch (aksiNyataSessionState) {
      case 'NOT_CREATED':
        cardUrl = `/${
          process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
          'pelatihan-mandiri'
        }/topik/${topikId}/aksi-nyata?${stringify({
          topik_name: name,
        })}`
      case 'CREATED':
        cardUrl = `/${
          process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
          'pelatihan-mandiri'
        }/topik/${topikId}/aksi-nyata?${stringify({
          topik_name: name,
        })}`
        break
      case 'NEED_REVISION':
      case 'APPEAL':
      case 'CONFIRMED':
        cardUrl = `/${
          process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
          'pelatihan-mandiri'
        }/topik/${topikId}/aksi-nyata/form?${stringify({ topik_name: name })}`
        break
      case 'VALIDATED':
      case 'COMPLETED':
        cardUrl =
          `/${
            process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
            'pelatihan-mandiri'
          }/aksi-nyata/${aksiNyataId}?` +
          stringify({ topik_name: name, topik_id: topikId })
        break
    }
    setAksiNyataUrl(cardUrl)
  }, [aksiNyataId, aksiNyataSessionState, name, topikId])

  if (aksiNyataSessionState === 'NEED_REVISION') {
    return (
      <NeedRevisionCard
        aksiNyataUrl={aksiNyataUrl}
        name={name}
        onCardClick={(url) => router.push(url)}
        selectedOptionTitle={selectedOptionTitle}
      />
    )
  }

  return (
    <div data-testid="aksinyata-card" className="py-5">
      <div className="mb-2">
        <Text variant="heading-md">Aksi Nyata</Text>
        <Text variant="body" color="subdued">
          Praktikkan pemahaman terhadap topik ini
        </Text>
      </div>
      <div className={isDesktop ? `grid grid-cols-2 gap-2` : null}>
        <div
          className={clsx(
            'relative flex p-4 w-full mb-3 bg-white rounded border border-subdued',
            'hover:cursor-pointer'
          )}
          onClick={
            isOnAppeal
              ? () => {
                  toast({
                    message:
                      'Aksi Nyata Anda masih dalam proses pengajuan banding',
                  })
                }
              : null
          }
        >
          {['COMPLETED', 'VALIDATED', 'APPEAL'].includes(
            aksiNyataSessionState
          ) ? (
            <div className="w-8 h-8 rounded-full flex flex-grow-0 flex-shrink-0 items-center justify-center bg-surface-success-default">
              <div className="w-5 h-5 rounded-full flex items-center justify-center bg-icon-success">
                <Icon
                  data-testid="completed_indicator"
                  as="i"
                  color="inverse"
                  fontSize="sm"
                >
                  check
                </Icon>
              </div>
            </div>
          ) : (
            <div className="flex-grow-0 flex-shrink-0">
              <div
                className={clsx(
                  'flex justify-center items-center w-8 h-8 rounded-full overflow-hidden',
                  'bg-expressive-dark-blue text-white'
                )}
              >
                <Icon
                  data-testid="unlocked_indicator"
                  as="i"
                  color="inverse"
                  fontSize="sm"
                >
                  batch_prediction
                </Icon>
              </div>
            </div>
          )}
          <div className="ml-4 flex-auto">
            <div className="mb-[2px]">
              <Text
                data-testid="card-link"
                data-card-url={aksiNyataUrl}
                onClick={() => {
                  GATrackEvent({
                    eventName: 'learning_activity',
                    eventParams: {
                      event_trigger: 'click_aksi_nyata',
                      action_type: [
                        'COMPLETED',
                        'VALIDATED',
                        'APPEAL',
                      ].includes(aksiNyataSessionState)
                        ? 'view_aksi_nyata'
                        : 'do_aksi_nyata',
                      meta: JSON.stringify({
                        topik_id: topikId,
                      }),
                    },
                    sendDefaultParams: true,
                  })
                  router.push(aksiNyataUrl)
                }}
                className={clsx(
                  'overlay-link cursor-pointer',
                  isOnAppeal && 'pointer-events-none'
                )}
                variant="body-sm"
              >
                Aksi Nyata
              </Text>
            </div>
            <div className="mb-[2px] leading-tight">
              <Text variant="heading-md">{name}</Text>
            </div>
            <div className="flex items-center">
              {aksiNyataSessionState === 'NOT_CREATED' && (
                <Icon className="mr-2" as="i" color="default" fontSize="sm">
                  batch_prediction
                </Icon>
              )}
              <div>
                <Text
                  className="line-clamp-1"
                  as="span"
                  variant="body-sm"
                  color="subdued"
                >
                  {!!selectedOptionTitle && !isLocked
                    ? selectedOptionTitle
                    : `${optionsAmount} pilihan Aksi Nyata`}
                </Text>
              </div>
            </div>
            <div className="mt-3">
              {isLocked && <Badge>Lihat Pilihan Aksi Nyata</Badge>}
              {!isLocked &&
                !['COMPLETED', 'VALIDATED', 'APPEAL'].includes(
                  aksiNyataSessionState
                ) && (
                  <Badge data-testid="aksinyata-status" variant="informational">
                    {aksiNyataSessionState === 'NOT_CREATED'
                      ? 'Pilih Aksi Nyata'
                      : aksiNyataSessionState === 'CREATED'
                      ? 'Lengkapi Lembar Aksi Nyata'
                      : 'Kumpulkan Lembar Aksi Nyata'}
                  </Badge>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AksiNyataCard
